import { flush } from '@sentry/react';

import { trpcClient } from '@web/utils/trpc/trpcClient';

import { getAuth, setAuth } from './manager';

// Last issued nonce { nonce, signature }. The SIWE flow is two-step:
//   1) getNonce()                 → server returns { nonce, signature }
//   2) signInWithWallet(message, signature)
// The signature on the nonce must round-trip back to /auth-siwe so the edge
// function can re-verify the HMAC. We stash the nonceSignature here between
// the two calls, scoped to the in-flight sign-in attempt.
let lastNonceSignature: string | null = null;

export async function fetchNonce(): Promise<string> {
  const r = await trpcClient.auth.getNonce.query();
  lastNonceSignature = r.signature;
  return r.nonce;
}

export async function signInWithWallet(message: string, signature: string) {
  const nonceSignature = lastNonceSignature;
  if (!nonceSignature) {
    throw new Error('Sign-in flow used without prior getNonce');
  }
  // Pull the nonce out of the SIWE message (it's required by the edge fn
  // alongside the original message for cross-verification).
  const nonceMatch = message.match(/Nonce:\s*(\S+)/);
  if (!nonceMatch?.[1]) {
    throw new Error('Could not extract nonce from SIWE message');
  }
  const nonce = nonceMatch[1];
  setAuth(
    await trpcClient.auth.signInWithWallet.mutate({
      message,
      signature,
      nonce,
      nonceSignature,
    }),
  );
  lastNonceSignature = null;
}

export async function signOut() {
  await flush().catch(() => null);

  const auth = getAuth();
  if (auth) {
    await trpcClient.auth.signOut
      .mutate({ refresh: auth.refresh })
      .catch(() => null);
  }
  localStorage.clear();
  window.dispatchEvent(new StorageEvent('local-storage'));
}

export async function refreshAuth() {
  const auth = getAuth();
  if (!auth) {
    throw new Error('User must be authenticated to refresh auth');
  }

  const newAuth = await trpcClient.auth.refreshAuthCredentials.mutate({
    access: auth.access,
    refresh: auth.refresh,
  });
  setAuth(newAuth);
  return newAuth;
}
